import React from 'react';
import classnames from 'classnames';
import styles from './checkDescription.module.scss';
import { unit } from 'mathjs';
import { Button, FormGroup } from 'react-bootstrap';
import { BsArrowClockwise } from 'react-icons/bs';
import { Control, FieldErrors, useController, useWatch } from 'react-hook-form';
import { useMutation } from 'react-query';
import { FeatureToggle } from 'react-feature-toggles';
import { FEATURE_FLAGS } from 'utils/constants';
import restFactory from 'v2/utils/restFactory';
import { CreateSearchFormData } from '../CreateSearchSchema';
import { CheckRequestDescription, CheckDescriptionStatus } from 'v2/models';
import { DataProductFormData } from 'v2/DataProducts/models';

export const titles: Record<CheckDescriptionStatus, string> = {
  default: 'Please check your description.',
  good: 'Your description looks good!',
  bad: 'Your description could be improved.',
  error: 'Whoops! Something went wrong.',
};

export enum DescriptionType {
  DATA_PRODUCT = 'data_product',
  REQUEST = 'request',
  INTERNAL_REQUEST = 'internal_request',
}

const featureFlag = {
  [DescriptionType.DATA_PRODUCT]: {
    name: FEATURE_FLAGS.CHECK_DATA_PRODUCT_DESCRIPTION,
    endpoint: 'check_dataproduct_description',
  },
  [DescriptionType.REQUEST]: {
    name: FEATURE_FLAGS.CHECK_REQUEST_DESCRIPTION,
    endpoint: 'check_request_description',
  },
  [DescriptionType.INTERNAL_REQUEST]: {
    name: FEATURE_FLAGS.CHECK_REQUEST_DESCRIPTION,
    endpoint: 'check_internal_request_description',
  },
};

interface CheckDescriptionProps {
  control: Control<CreateSearchFormData | DataProductFormData>;
  errors: FieldErrors<CreateSearchFormData | DataProductFormData>;
  type: DescriptionType;
  hideDescriptionForBadStatus?: boolean;
}

const CheckDescription = ({
  control,
  errors,
  type,
  hideDescriptionForBadStatus = false,
}: CheckDescriptionProps) => {
  const description = useWatch({ control, name: 'description' });
  const {
    field: { onChange: setStatus, value: status },
  } = useController({ name: 'check_description_status', control });
  const {
    field: { onChange: setFeedback, value: feedback },
  } = useController({ name: 'check_description_feedback', control });

  const {
    field: { onChange: setHistoryId },
  } = useController({ name: 'prompt_history_id', control });

  const { isLoading: isCheckDescriptionLoading, mutate: checkDescription } =
    useMutation(
      async (prompt: string) => {
        const urlbase = `/dj/api/v1/gpt/`;
        return await restFactory.get<CheckRequestDescription>(
          `${urlbase}${featureFlag[type].endpoint}`,
          { prompt },
          undefined,
          { timeout: unit('15s').toNumber('ms') }
        );
      },
      {
        onSuccess: (response) => {
          setStatus(
            CheckDescriptionStatus[
              response.content.result as CheckDescriptionStatus
            ] || CheckDescriptionStatus.error
          );
          setFeedback(response.content.feedback || '');
          setHistoryId(response.prompt_history_id);
        },
        onError: () => {
          setStatus(CheckDescriptionStatus.error);
          setFeedback(
            'Our description checker is not working at the moment. Please proceed to the next step below.'
          );
        },
      }
    );

  const handleCheckDescription = () => {
    checkDescription(description);
  };

  return (
    <FeatureToggle featureName={featureFlag[type].name}>
      <FormGroup className="mt-4">
        <div
          role="alert"
          className={classnames(
            'd-flex flex-column',
            styles.checkDescriptionContainer,
            {
              [styles.error]: status === CheckDescriptionStatus.bad,
              [styles.warning]: status === CheckDescriptionStatus.error,
            }
          )}
          data-cy="description-check-container"
        >
          <div
            className={classnames(
              'd-flex flex-wrap align-items-center justify-content-between',
              styles.check
            )}
          >
            <span className={classnames(styles.title)}>
              {titles[status || CheckDescriptionStatus.default]}
            </span>
            <Button
              type="button"
              onClick={handleCheckDescription}
              disabled={isCheckDescriptionLoading || !description}
              data-cy="description-check-button"
              aria-pressed={status !== undefined}
            >
              {isCheckDescriptionLoading ? (
                <>
                  Checking
                  <BsArrowClockwise
                    className={classnames('ml-1', styles.loadingIcon)}
                  />
                </>
              ) : (
                'Check Description'
              )}
            </Button>
          </div>

          {errors?.check_description_status?.message && (
            <span className={styles.errorRequired}>
              {errors?.check_description_status?.message}
            </span>
          )}

          {status &&
            [CheckDescriptionStatus.bad, CheckDescriptionStatus.error].includes(
              status
            ) && (
              <div>
                <p>{feedback}</p>
                {status === CheckDescriptionStatus.bad &&
                  !hideDescriptionForBadStatus && (
                    <p>
                      We encourage you to make the above adjustments, but you
                      can also click next below and roll the dice.
                    </p>
                  )}
              </div>
            )}
        </div>
      </FormGroup>
    </FeatureToggle>
  );
};

export default CheckDescription;
